import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";
import type { DuelLog, Side } from "@/duels/registry";

export type DuelStatus =
  "invited" | "ready" | "active" | "finished" | "declined" | "cancelled" | "expired" | "aborted";

export type TimeControl =
  | { kind: "correspondence"; days: number }
  | { kind: "clock"; base_s: number; increment_s: number }
  | { kind: "realtime" };

export type Pace = "correspondence" | "blitz" | "rapid" | "realtime";

export interface Person {
  id: number;
  name: string;
}

export interface DuelSummary {
  id: number;
  game_key: string;
  status: DuelStatus;
  time_control: TimeControl;
  pace: Pace;
  is_rated: boolean;
  challenger: Person | null;
  invitee: Person | null;
  white: Person | null;
  black: Person | null;
  my_side: Side | null;
  is_challenger: boolean;
  to_move: Side | null;
  your_turn: boolean;
  deadline_at: string | null;
  result: Side | "draw" | null;
  result_reason: string | null;
  rating_change: Partial<Record<Side, number>>;
  created_at: string;
  finished_at: string | null;
}

export interface DuelDetail extends DuelSummary {
  game_version: number;
  realtime: boolean;
  draw_offers: boolean;
  ratings: Partial<Record<Side, number>>;
  view: unknown;
  moves: string[];
  clock: Record<Side, number> | null;
  /** A clock duel: when the side to move started thinking. A realtime one: its start. */
  turn_started_at: string | null;
  draw_offer: Side | null;
  present: Side[];
  logs_sent: Side[];
  server_now: string;
  /** A realtime duel, once started: deals both games. */
  seed?: number;
}

export interface DuelGame {
  key: string;
  version: number;
  time_controls: TimeControl["kind"][];
  realtime: boolean;
  draw_offers: boolean;
  my_ratings: Partial<Record<Pace, number>>;
  your_turn: number;
}

export interface DuelHub {
  games: DuelGame[];
  running: DuelSummary[];
  open: DuelSummary[];
  recent: DuelSummary[];
  server_now: string;
}

export interface RatingEntry {
  rank: number;
  participant: string;
  rating: number;
  games: number;
  wins: number;
  draws: number;
  losses: number;
  is_me: boolean;
}

export interface RatingBoard {
  pace: Pace;
  paces: Pace[];
  entries: RatingEntry[];
}

export interface NewChallenge {
  game_key: string;
  opponent_id: number | null;
  time_control: TimeControl;
  side: Side | "random";
  rated: boolean;
}

export function useDuelHub(game?: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: game ? ["duels", "hub", game] : ["duels", "hub"],
    queryFn: () => api<DuelHub>(game ? `/api/duels/?game=${game}` : "/api/duels/"),
    enabled,
    // Socket closed: challenges and turns still show up.
    refetchInterval: 30_000,
  });
}

export function useDuelInbox({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["duels", "inbox"],
    queryFn: () => api<{ your_turn: number; invitations: number }>("/api/duels/inbox/"),
    enabled,
    refetchInterval: 60_000,
  });
}

/** One duel. With the socket closed it polls: every 2 s on the clock, 15 s by correspondence. */
export function useDuel(id: number, socketOpen: boolean) {
  return useQuery({
    queryKey: ["duels", "duel", id],
    queryFn: () => api<DuelDetail>(`/api/duels/${id}/`),
    refetchInterval: (query) =>
      socketOpen ? false : query.state.data?.time_control.kind === "correspondence" ? 15_000 : 2000,
  });
}

export function useRatings(key: string, pace: Pace | null) {
  return useQuery({
    queryKey: ["duels", "ratings", key, pace],
    queryFn: () => api<RatingBoard>(`/api/duels/${key}/ratings/${pace ? `?pace=${pace}` : ""}`),
  });
}

export function searchOpponents(query: string): Promise<{ results: Person[] }> {
  return api(`/api/duels/opponents/?q=${encodeURIComponent(query)}`);
}

export function useCreateDuel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (challenge: NewChallenge) =>
      api<DuelDetail>("/api/duels/", { method: "POST", json: challenge }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["duels"] }),
  });
}

type Action = "accept" | "decline" | "resign";

/** Any of the duel's own actions; the answer is the duel as it now stands. */
export function useDuelAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: Action }) =>
      api<DuelDetail>(`/api/duels/${id}/${action}/`, { method: "POST" }),
    onSuccess: (duel) => {
      queryClient.setQueryData(["duels", "duel", duel.id], duel);
      void queryClient.invalidateQueries({ queryKey: ["duels", "hub"] });
      void queryClient.invalidateQueries({ queryKey: ["duels", "inbox"] });
    },
  });
}

export function postMove(id: number, move: string): Promise<DuelDetail> {
  return api<DuelDetail>(`/api/duels/${id}/moves/`, { method: "POST", json: { move } });
}

export function postDraw(id: number, action: "offer" | "accept" | "decline"): Promise<DuelDetail> {
  return api<DuelDetail>(`/api/duels/${id}/draw/`, { method: "POST", json: { action } });
}

export function postLog(id: number, log: DuelLog): Promise<DuelDetail> {
  return api<DuelDetail>(`/api/duels/${id}/log/`, { method: "POST", json: log });
}

export const duelTopic = (id: number): string => `duel:${id}`;
export const inboxTopic = (userId: number): string => `duels:${userId}`;
export const LOBBY_TOPIC = "duels:lobby";

/** Who the other player is, from this player's side. */
export function opponentOf(duel: DuelSummary): Person | null {
  if (duel.my_side === "white") return duel.black;
  if (duel.my_side === "black") return duel.white;
  return duel.is_challenger ? duel.invitee : duel.challenger;
}
